/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed, type Ref, type ComputedRef } from "vue";
import type { CalendarRequestPayload } from "@/types/domain";
import { toStartOfDayIso } from "@/helpers/dateHelper";
import {
  normalizeCalendarResponse,
  cleanCalendarParams,
} from "@/helpers/availabilityCalendarHelper";
import { resolveCalendarShiftDate } from "@/helpers/calendarNavigationHelper";
import { ensureCenterVariantKeys } from "@/helpers/bookingOptionHelper";
import {
  applyCalendarResultToDoctor,
  applyCalendarErrorToDoctor,
} from "@/helpers/availabilityCalendarUpdateHelper";
import { getAvailabilityCalendar } from "@/services/naaviApi/internalService";
import { getDoctorKey } from "@/helpers/doctorSelectionHelper";

const SEARCH_TYPE_FULL_DAY = 2;
const DEFAULT_CALENDAR_DAYS = 4;

export interface CalendarNavigationOptions {
  doctors: Ref<any[]>;
  currentWindowStart: Ref<Date | null>;
  filters: ComputedRef<{
    online: boolean;
    therapy?: string | null;
    therapyType?: string | null;
    location?: {
      lat?: number | null;
      lng?: number | null;
    };
    radiusKm?: number | null;
  }>;
}

export function useCalendarNavigation(options: CalendarNavigationOptions) {
  const { doctors, currentWindowStart, filters } = options;

  const calendarLoadingCount = ref(0);
  const calendarRequestsInFlight = ref<Record<string, boolean>>({});

  const isSameDoctorEntry = (a: any, b: any): boolean => {
    if (!a || !b) return false;
    return getDoctorKey(a) === getDoctorKey(b);
  };

  const findDoctorIndex = (doctor: any): number => {
    return doctors.value.findIndex((item) => isSameDoctorEntry(item, doctor));
  };

  const buildCalendarParams = (baseDate: Date, days: number) => {
    const hasGeo =
      !filters.value.online &&
      filters.value.location?.lat != null &&
      filters.value.location?.lng != null;

    return cleanCalendarParams({
      date: toStartOfDayIso(baseDate),
      searchType: SEARCH_TYPE_FULL_DAY,
      concept: 0,
      therapy: filters.value.therapy || undefined,
      therapyType: filters.value.therapyType || undefined,
      latitude: hasGeo ? filters.value.location!.lat : undefined,
      longitude: hasGeo ? filters.value.location!.lng : undefined,
      radius: hasGeo ? 50 : undefined,
      days,
    });
  };

  const applyCalendarResult = (
    doctorIndex: number,
    optionIndex: number,
    calendarResult: any,
    fallbackBaseDateIso: string | undefined,
  ) => {
    const doctorEntry = doctors.value[doctorIndex];
    if (!doctorEntry) return;

    const updateResult = applyCalendarResultToDoctor({
      doctorEntry,
      optionIndex,
      calendarResult: calendarResult as any,
      fallbackBaseDateIso,
      ensureCenterVariantKeys: ensureCenterVariantKeys as any,
    });

    if (!updateResult) return;

    if (calendarResult.windowDate) {
      currentWindowStart.value = calendarResult.windowDate;
    }

    const updatedDoctors = [...doctors.value];
    updatedDoctors.splice(doctorIndex, 1, updateResult.doctor);
    doctors.value = updatedDoctors;
  };

  const applyCalendarError = (
    doctorIndex: number,
    optionIndex: number,
    message: string,
  ) => {
    const doctorEntry = doctors.value[doctorIndex];
    if (!doctorEntry) return;

    const updateResult = applyCalendarErrorToDoctor(
      doctorEntry,
      optionIndex,
      message,
    );
    if (!updateResult) return;

    const updatedDoctors = [...doctors.value];
    updatedDoctors.splice(doctorIndex, 1, updateResult.doctor);
    doctors.value = updatedDoctors;
  };

  const fetchDoctorCalendar = async (
    doctor: any,
    payload: CalendarRequestPayload,
  ) => {
    const doctorIndex = findDoctorIndex(doctor);
    if (doctorIndex === -1) return;

    const doctorEntry = doctors.value[doctorIndex];
    const bookingKey =
      payload.bookingKey ?? doctorEntry.bookingOptions?.[0]?.key ?? null;

    if (!bookingKey) return;

    const optionList = doctorEntry.bookingOptions ?? [];
    const optionIndex = optionList.findIndex(
      (opt: any) => String(opt.key) === String(bookingKey),
    );
    if (optionIndex === -1) return;

    const option = optionList[optionIndex];
    if (!payload.force && option.scheduleLoaded) return;
    if (option.scheduleLoading) return;

    const baseDate = payload.date
      ? new Date(payload.date as string)
      : currentWindowStart.value ?? new Date();
    baseDate.setHours(0, 0, 0, 0);

    const params = buildCalendarParams(
      baseDate,
      payload.days ?? DEFAULT_CALENDAR_DAYS,
    );

    const tenantKey =
      doctorEntry.tenantXId ??
      doctorEntry.tenantId ??
      doctorEntry.rawSummary?.tenantXId;

    if (!tenantKey) return;

    const doctorToken = doctorEntry.entryKey ?? doctorEntry.id ?? doctorIndex;
    const requestKey = `${doctorToken}:${bookingKey}:${params.date ?? ""}`;
    if (calendarRequestsInFlight.value[requestKey]) return;
    calendarRequestsInFlight.value[requestKey] = true;

    // Mark loading state
    const targetDoctor = { ...doctorEntry };
    const bookingOptions = [...targetDoctor.bookingOptions];
    const previousSchedule = option.schedule ?? [];
    const targetOption = {
      ...option,
      scheduleLoading: true,
      scheduleError: null,
    };
    bookingOptions.splice(optionIndex, 1, targetOption);
    targetDoctor.bookingOptions = bookingOptions;

    const updatedDoctors = [...doctors.value];
    updatedDoctors.splice(doctorIndex, 1, targetDoctor);
    doctors.value = updatedDoctors;
    calendarLoadingCount.value += 1;

    try {
      const response = await getAvailabilityCalendar(
        tenantKey,
        doctorEntry.resourceId,
        targetOption.bookingTypeId,
        params,
      );

      const calendarResult = normalizeCalendarResponse({
        response,
        previousSchedule,
        merge: false,
        fallbackBaseDate: baseDate,
        fallbackDays: params.days as number,
        fallbackBaseDateIso: params.date as string,
        currentPriceLocalized: targetOption.priceLocalized,
        currentPriceLabel: targetOption.price,
      });

      applyCalendarResult(
        doctorIndex,
        optionIndex,
        calendarResult,
        params.date as string,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Calendar unavailable";
      applyCalendarError(doctorIndex, optionIndex, message);
    } finally {
      calendarLoadingCount.value = Math.max(0, calendarLoadingCount.value - 1);
      delete calendarRequestsInFlight.value[requestKey];
    }
  };

  const handleRequestMoreDates = async (
    doctor: any,
    payload: CalendarRequestPayload,
  ) => {
    const bookingOptions = doctor.bookingOptions ?? [];
    const targetOption =
      bookingOptions.find(
        (opt: any) => String(opt.key) === String(payload.bookingKey),
      ) ?? bookingOptions[0];

    if (!targetOption) return;

    const schedule = targetOption.schedule ?? [];
    let resolvedTargetDate: Date | null = null;

    if (payload.targetDate) {
      const candidate = new Date(`${payload.targetDate}T00:00:00`);
      if (!Number.isNaN(candidate.getTime())) {
        resolvedTargetDate = candidate;
      }
    }

    const targetDateValue =
      resolvedTargetDate ??
      resolveCalendarShiftDate({
        direction: payload.direction ?? null,
        schedule,
        firstDate: payload.firstDate ?? null,
        lastDate: payload.lastDate ?? null,
        days: DEFAULT_CALENDAR_DAYS,
      });

    if (!targetDateValue) return;

    const targetDateIso = toStartOfDayIso(targetDateValue);
    if (doctor.baseDateIso === targetDateIso) return;

    await fetchDoctorCalendar(doctor, {
      bookingKey: payload.bookingKey,
      date: targetDateValue,
      days: DEFAULT_CALENDAR_DAYS,
      force: true,
      direction: payload.direction,
    });
  };

  const handleRequestCalendar = async (
    doctor: any,
    payload: CalendarRequestPayload,
  ) => {
    await fetchDoctorCalendar(doctor, payload);
  };

  return {
    calendarLoadingCount,
    calendarRequestsInFlight,
    fetchDoctorCalendar,
    handleRequestMoreDates,
    handleRequestCalendar,
    isSameDoctorEntry,
  };
}
